import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MobileMenu from "../components/MobileMenu";
import Overlay from "../components/Overlay";
import Footer from "../components/Footer";
import Header from "../components/Header";
import Toast from "../components/toast";
import { Project } from "../type";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import { ChevronDown } from "lucide-react";

const slides = [
    "./assets/img/slide1.jpg",
    "./assets/img/slide2.jpg",
    "./assets/img/slide3.jpg"
];

type SortKey = "id" | "date" | "available_spots";

const sortOptions: { label: string; value: SortKey }[] = [
    { label: "جدیدترین", value: "id" },
    { label: "تاریخ", value: "date" },
    { label: "ظرفیت", value: "available_spots" },
];

export default function Dashboard() {
    const { isAuthenticated, accessToken, loading: loadingauth } = useAuth();
    const navigate = useNavigate();

    const [menuOpen, setMenuOpen] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [popupOpen, setPopupOpen] = useState(false);

    const [selectedSession, setSelectedSession] = useState<Project>();
    const [activeTab, setActiveTab] = useState("sessions");

    const [activeTabFooter, setActiveTabFooter] = useState("dashboard");

    const [sessions, setSessions] = useState<Project[]>();
    const [videos, setVideos] = useState<Project[]>();
    const [loading, setLoading] = useState(true);

    const [sortBy, setSortBy] = useState<SortKey>("id");
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const [toast, setToast] = useState<{ title: string, text: string }>();
    const [toastshow, setToastshow] = useState(false);

    useEffect(() => {
        if (loadingauth) return;

        if (!isAuthenticated) {
            navigate('/');
            return;
        }

        const fetchsessions = async () => {
            try {
                const mainRes = await api.get("/sessions", {
                    headers: {
                        Authorization: accessToken
                            ? `Bearer ${accessToken}`
                            : "",
                    },
                })

                if (mainRes.data?.data) {
                    setSessions(mainRes.data.data);
                }

            } catch (err) {
                console.log("sessions fetch error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchsessions();

    }, [isAuthenticated, loadingauth]);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide(prev => (prev + 1) % slides.length);
        }, 6000);
        return () => clearInterval(interval);
    }, []);

    function openPopup(item: Project) {
        if (item) {
            setSelectedSession(item);
            setPopupOpen(true);
        }
    }

    function closePopup() {
        setPopupOpen(false);
    }

    async function handleAddToCart(session: Project) {
        try {
            const mainRes = await api.post("/cart", {
                product_id: session.product_id,
                session_id: session.id
            }, {
                headers: {
                    Authorization: accessToken
                        ? `Bearer ${accessToken}`
                        : "",
                },
            })

            if (mainRes.status === 200) {
                setSelectedSession(undefined);
                setPopupOpen(false);
                setToastshow(true);
                setToast({ title: 'موفقیت', text: "با موفقیت به سبد خرید اضافه شد." });
            }

            if (mainRes.data.error === "No available spot is left.") {
                setSelectedSession(undefined);
                setPopupOpen(false);
                setToastshow(true);
                setToast({ title: 'خطا', text: "ظرفیت بیشتری در دسترس نیست." });
            }

            if (mainRes.data.error === "Spot is currently booked.") {
                setSelectedSession(undefined);
                setPopupOpen(false);
                setToastshow(true);
                setToast({ title: 'خطا', text: "این برنامه از قبل رزرو شده یا در سبد خرید شماست." });
            }

        } catch (err) {
            console.log("Service fetch error:", err);
        }
    }

    const formatPersianDate = (dateString: string): string | undefined => {
        if (!dateString) return undefined;

        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
            console.warn('Invalid date:', dateString);
            return undefined;
        }

        try {
            return new Intl.DateTimeFormat('fa-IR', {
                calendar: 'persian',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }).format(date);
        } catch (err) {
            console.warn('Intl persian calendar not supported, falling back:', err);
            try {
                return new Intl.DateTimeFormat('fa-IR', {
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                }).format(date);
            } catch {
                return date.toLocaleDateString();
            }
        }
    };

    const sortList = (list: Project[]) =>
        [...list].sort((a, b) => {
            if (sortBy === "date") {
                const aVal = new Date(a.date).getTime();
                const bVal = new Date(b.date).getTime();
                return (isNaN(bVal) ? 0 : bVal) - (isNaN(aVal) ? 0 : aVal);
            }

            if (sortBy === "available_spots") {
                const aVal = parseFloat(String(a.available_spots ?? 0));
                const bVal = parseFloat(String(b.available_spots ?? 0));
                return bVal - aVal;
            }

            const aNum = Number(a.id);
            const bNum = Number(b.id);
            if (!isNaN(aNum) && !isNaN(bNum)) {
                return bNum - aNum;
            }
            return String(b.id).localeCompare(String(a.id));
        });

    const currentList = useMemo(() => {
        const list = activeTab === "sessions" ? sessions : videos;
        return list ? sortList(list) : list;
    }, [activeTab, sessions, videos, sortBy]);

    const activeSortLabel = sortOptions.find((o) => o.value === sortBy)?.label;

    return (
        <div className="min-h-dvh bg-darkbg">
            <div className="relative w-full max-w-[450px] mx-auto min-h-dvh max-h-dvh flex flex-col gap-2 pb-[72px] overflow-x-hidden">

                <Overlay active={menuOpen} close={() => setMenuOpen(false)} />
                <MobileMenu active={menuOpen} />
                <Header menuOpen={menuOpen} close={() => setMenuOpen(!menuOpen)} />

                <div>
                    <section className="w-full h-[20vh] overflow-hidden">
                        <div className="relative w-full h-full border-b-4 border-[#f4b400]">
                            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent z-[5]" />

                            {slides.map((slide, index) => (
                                <img
                                    key={index}
                                    src={slide}
                                    alt="slide"
                                    className={[
                                        "absolute inset-0 w-full h-full object-cover",
                                        "transition-opacity duration-700 ease-in-out",
                                        index === currentSlide ? "opacity-100 z-10" : "opacity-0"
                                    ].join(" ")}
                                />
                            ))}

                            <div className="absolute bottom-0 left-0 right-0 h-[6px] bg-[#f4b400]/20 blur-[2px] z-[6]" />
                        </div>
                    </section>
                </div>

                <main className="flex-1 overflow-y-auto no-scrollbar">
                    <section className="flex-1 overflow-hidden scrollbar-thin">

                        <div className="px-4 pt-2 flex flex-col gap-2">
                            <div className="flex items-center gap-2 p-1 rounded-2xl bg-black/20 border border-primary/10">
                                <button
                                    onClick={() => {
                                        setActiveTab("videos");
                                    }}
                                    className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === "videos"
                                        ? "bg-primary text-black shadow-lg shadow-primary/20"
                                        : "text-white/60 hover:text-white"
                                        }`}
                                >
                                    دوره های آموزشی
                                </button>
                                <button
                                    onClick={() => {
                                        setActiveTab("sessions");
                                    }}
                                    className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === "sessions"
                                        ? "bg-primary text-black shadow-lg shadow-primary/20"
                                        : "text-white/60 hover:text-white"
                                        }`}
                                >
                                    برنامه ها
                                </button>
                            </div>

                            <div className="relative">
                                <button
                                    onClick={() => setDropdownOpen((p) => !p)}
                                    className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-black/20 border
                                     border-primary/10 text-white text-sm font-bold transition-all"
                                >
                                    <span>
                                        ترتیب بر اساس:{" "}
                                        <span className="text-primary">{activeSortLabel}</span>
                                    </span>
                                    <ChevronDown
                                        size={16}
                                        className={`text-primary transition-transform duration-300 ${dropdownOpen ? "rotate-180" : ""
                                            }`}
                                    />
                                </button>

                                {dropdownOpen && (
                                    <div className="absolute top-full mt-1 left-0 right-0 z-20 rounded-2xl bg-darkbg border border-primary/10 overflow-hidden shadow-xl">
                                        {sortOptions.map((opt) => (
                                            <button
                                                key={opt.value}
                                                onClick={() => {
                                                    setSortBy(opt.value);
                                                    setDropdownOpen(false);
                                                }}
                                                className={`w-full px-4 py-3 text-sm text-right transition-colors ${sortBy === opt.value
                                                    ? "bg-primary/15 text-primary font-bold"
                                                    : "text-white/70 hover:bg-white/5"
                                                    }`}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto scrollbar-thin">
                            {currentList && currentList.length > 0 ?
                                currentList.map((item, index) => (
                                    <div
                                        key={index}
                                        onClick={() => openPopup(item)}
                                        role="button"
                                        tabIndex={0}
                                        dir="ltr"
                                        onKeyDown={(e) => e.key === "Enter" && openPopup(item)}
                                        className=" group bg-[#1c1c1c] h-[95px] my-2 min-h-fit flex 
                            flex-row-reverse rounded-[18px] cursor-pointer 
                            relative overflow-hidden transition-all duration-300 
                            ease-in-out hover:shadow-xl hover:shadow-[#f4b400]/10 
                            hover:scale-[1.02] ring-1 ring-white/5 hover:ring-white/10"

                                    >
                                        <div className="absolute inset-0 bg-gradient-to-l from-white/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                                        <img
                                            className="w-[40%] h-full rounded-r-[18px] object-cover"
                                            src={item.image || "./assets/img/event1.png"}
                                            alt={item?.name || "event"}
                                        />

                                        <div dir="rtl" className="w-[60%] bg-[#f4b400]/90 rounded-l-[18px] p-[20px] relative">
                                            <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/10 rounded-l-[18px] pointer-events-none" />

                                            <div className="relative">
                                                <h3 className="text-[18px] font-bold leading-tight text-gray-800">
                                                    {item.name}
                                                </h3>

                                                <p className="text-[13px] text-gray-600 mt-1">
                                                    {formatPersianDate(item.date)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <div dir="rtl" className="rounded-2xl border border-dashed border-primary/20 bg-black/10 p-6 text-center text-white/60">
                                        {activeTab === 'sessions' ? " فعلا برنامه‌ای در دسترس نیست." : " فعلا دوره‌ای در دسترس نیست."}
                                    </div>
                                )}
                        </div>
                    </section>

                    {popupOpen && selectedSession && (
                        <>
                            <div
                                onClick={(e) => { if (e.target === e.currentTarget) closePopup(); }}
                                className="fixed inset-0 backdrop-blur-lg bg-black/60 flex justify-center items-center z-[200]"
                            />

                            <div
                                onClick={(e) => { if (e.target === e.currentTarget) closePopup(); }}
                                className="fixed inset-0 flex justify-center items-center z-[210] animate-popupFade px-3"
                            >
                                <div className="w-11/12 md:w-[55%] max-w-2xl rounded-[16px] overflow-hidden relative
                                  bg-[rgba(35,35,35,0.35)] backdrop-blur-xl
                                  ring-1 ring-white/10 shadow-2xl shadow-black/40">

                                    {/* close */}
                                    <button
                                        onClick={closePopup}
                                        className="absolute right-[12px] top-[10px] z-[2]
                                 w-[34px] h-[34px] rounded-full
                                 bg-black/40 hover:bg-black/60
                                 text-gray-200 text-3xl leading-[30px]"
                                        aria-label="close"
                                    >
                                        ×
                                    </button>

                                    <img
                                        src={selectedSession.image || "./assets/img/event1.png"}
                                        className="w-full h-[220px] object-cover"
                                        alt={selectedSession?.name || "event"}
                                    />

                                    <div dir="rtl" className="p-[16px_18px] text-right">
                                        <div className="flex justify-between items-center gap-3 mb-2">
                                            <h2 className="text-[#d2cfcf] text-[20px] font-bold leading-tight">
                                                {selectedSession.name}
                                            </h2>
                                            <span className="text-[#d2cfcf] text-[18px] font-bold whitespace-nowrap">
                                                {Number(selectedSession.price).toLocaleString("fa")} تومان
                                            </span>
                                        </div>

                                        <p dir="rtl" className="text-[#d2cfcf] text-[15px] mb-[18px] opacity-90">
                                            {formatPersianDate(selectedSession.date)}
                                        </p>
                                        <p className="text-[#d2cfcf] text-[15px] mb-[18px] opacity-90">ظرفیت باقی مانده : {selectedSession.available_spots}</p>
                                        {selectedSession.supervisor && <p className="text-[#d2cfcf] text-[15px] mb-[18px] opacity-90">سرپرست : {selectedSession.supervisor}</p>}

                                        <div className="flex justify-between items-center gap-3 mt-4">
                                            <button
                                                onClick={() => { handleAddToCart(selectedSession) }}
                                                className="bg-[#f4b400] px-[20px] py-[16px] text-[18px] rounded-[12px]
                                     flex-1 transition-all duration-300 ease-in-out
                                     hover:shadow-xl hover:shadow-[#f4b400]/20 hover:scale-[1.01]
                                     active:scale-[0.99]"
                                            >
                                                افزودن به سبد خرید
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                </main>

                <Toast active={toastshow} title={toast?.title} text={toast?.text} setToastshow={setToastshow} />
                <Footer activeTab={activeTabFooter} setActiveTab={setActiveTabFooter} />
            </div>
        </div>
    );

}